/**
 * Role profiles for the Super Admin "View app as" control.
 *
 * The backend independently enforces the selected role. This module projects
 * the visible controls from the backend's effective permissions.
 */

export type RolePreview = 'super_admin' | 'regular' | 'ce' | 'ra' | 'dcma' | 'admin';

export interface UserPermissionsProjection {
  allowed_workflow_types: string[] | null;
  allow_chatbot?: boolean;
  allow_dcma_pbp?: boolean;
  allow_dcma_crr?: boolean;
  can_use_standalone_price_estimator?: boolean;
  market_research_view?: 'customer' | 'contracting' | 'unavailable' | 'unknown';
  customer_market_research_enabled?: boolean;
  allow_changelog?: boolean;
  /** User-specific entitlement; it is deliberately never simulated. */
  allow_training?: boolean;
  allow_ra_training?: boolean;
}

export interface RolePreviewInput {
  permissions: UserPermissionsProjection | null | undefined;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isUserFeedbackStaff: boolean;
}

export interface PresentationCapabilities {
  allowedWorkflowTypes: string[] | null;
  allowChatbot: boolean;
  allowDcmaPbp: boolean;
  allowDcmaCrr?: boolean;
  canUseStandalonePriceEstimator: boolean;
  marketResearchView: 'customer' | 'contracting' | 'unavailable' | 'unknown';
  showAdminNavigation: boolean;
  /** Unit-scoped reporting is available to Unit Managers and its preview. */
  showUsageDashboard: boolean;
  showSuperAdminControls: boolean;
  showUserFeedback: boolean;
  allowChangelog: boolean;
  showAllWorkflows: boolean;
  readOnly: boolean;
  isRolePreview: boolean;
  /** Convenience alias for existing Training gates. */
  allowTraining: boolean;
  allowRaTraining: boolean;
  /** The real user's named-feature entitlements, safe to consume in default view only. */
  userFeatureEntitlements: {
    allowTraining: boolean;
  };
}

/**
 * The App-shell state that can become unavailable after a presentation-only
 * role change. This is deliberately UI-only: it must never be sent to an API
 * or used as an authorization decision.
 */
export interface RolePreviewNavigationInput {
  appPage: 'type_selector' | 'flow_selector' | 'workflow' | 'training' | 'ra_training';
  targetCapabilities: PresentationCapabilities;
  workflowType?: string | null;
  /** Retained for callers that still carry a stage cursor. Navigation reloads workflows. */
  marketResearchBackendStage?: number | null;
  flowSelectorScope?: string | null;
  showDashboard?: boolean;
  showManageUsers?: boolean;
  supportIssueView?: 'customer' | 'admin' | null;
}

export interface RolePreviewNavigationPlan {
  destination: 'preserve' | 'home';
  /** Undefined means retain the existing scope; null means clear it. */
  flowSelectorScope: string | null | undefined;
}

export const ROLE_PREVIEW_OPTIONS: ReadonlyArray<{ value: RolePreview; label: string }> = [
  { value: 'super_admin', label: 'Super Admin (default)' },
  { value: 'regular', label: 'Contracting Officer/Specialist' },
  { value: 'ce', label: 'Civil Engineering' },
  { value: 'ra', label: 'Resource Advisor / Customer' },
  { value: 'dcma', label: 'DCMA' },
  { value: 'admin', label: 'Unit Manager' },
];

const isKnownPreview = (preview: string): preview is RolePreview =>
  ROLE_PREVIEW_OPTIONS.some((option) => option.value === preview);

const hasPermissions = (permissions: RolePreviewInput['permissions']): permissions is UserPermissionsProjection =>
  Boolean(permissions) && (
    permissions!.allowed_workflow_types === null
    || Array.isArray(permissions!.allowed_workflow_types)
  );

/** True for every safe, non-default presentation profile. */
export const isRolePreview = (preview: RolePreview | string): boolean => preview !== 'super_admin';

export const isReadOnlyRolePreview = (_preview: RolePreview | string): boolean => false;

/** The selector is an authenticated Super Admin affordance, never an admin one. */
export const canShowRolePreviewControl = <T extends { isSuperAdmin: boolean }>(input: T): boolean =>
  input.isSuperAdmin === true;

/**
 * A small common predicate for shell/menu affordances.  It prevents consumers
 * from restoring raw role checks next to the central role projection.
 */
export const canShowRolePreviewAffordance = (
  capabilities: PresentationCapabilities,
  affordance: 'adminNavigation' | 'usageDashboard' | 'superAdminControls' | 'userFeedback' | 'allWorkflows' | 'training',
): boolean => {
  switch (affordance) {
    case 'adminNavigation': return capabilities.showAdminNavigation;
    case 'usageDashboard': return capabilities.showUsageDashboard;
    case 'superAdminControls': return capabilities.showSuperAdminControls;
    case 'userFeedback': return capabilities.showUserFeedback;
    case 'allWorkflows': return capabilities.showAllWorkflows;
    case 'training': return capabilities.allowTraining;
  }
};

const targetCanUseWorkflowType = (
  capabilities: PresentationCapabilities,
  workflowType: string | null | undefined,
): boolean => {
  if (!workflowType) return false;
  if (workflowType === 'dcma_crr') return capabilities.allowDcmaCrr === true && (!capabilities.allowedWorkflowTypes || capabilities.allowedWorkflowTypes.includes(workflowType));
  if (workflowType === 'dcma_pbp') return capabilities.allowDcmaPbp
    && (!capabilities.allowedWorkflowTypes || capabilities.allowedWorkflowTypes.includes(workflowType));
  return !capabilities.allowedWorkflowTypes
    || capabilities.allowedWorkflowTypes.includes(workflowType);
};

/**
 * Computes whether a role-preview transition can retain the current UI
 * location. Open workflows are reopened from home after a role change so
 * their server-owned stage path and metadata are fetched under the new role.
 */
export const planRolePreviewNavigation = (
  input: RolePreviewNavigationInput,
): RolePreviewNavigationPlan => {
  const { targetCapabilities } = input;
  const home: RolePreviewNavigationPlan = { destination: 'home', flowSelectorScope: undefined };

  if (input.showDashboard && !targetCapabilities.showUsageDashboard) {
    return home;
  }
  if (input.showManageUsers && !targetCapabilities.showAdminNavigation) {
    return home;
  }
  if (input.supportIssueView === 'admin' && !targetCapabilities.showSuperAdminControls) return home;
  if (input.appPage === 'training') {
    return targetCapabilities.allowTraining
      ? { destination: 'preserve', flowSelectorScope: undefined }
      : home;
  }
  if (input.appPage === 'ra_training') {
    return targetCapabilities.allowRaTraining
      ? { destination: 'preserve', flowSelectorScope: undefined }
      : home;
  }
  if (input.appPage !== 'workflow') {
    if (input.appPage === 'flow_selector'
      && input.flowSelectorScope
      && !targetCanUseWorkflowType(targetCapabilities, input.flowSelectorScope)) {
      return { destination: 'preserve', flowSelectorScope: null };
    }
    if (input.appPage === 'flow_selector') {
      return { destination: 'preserve', flowSelectorScope: input.flowSelectorScope ?? null };
    }
    return { destination: 'preserve', flowSelectorScope: undefined };
  }
  return home;
};

const failClosedCapabilities = (preview: string): PresentationCapabilities => ({
  allowedWorkflowTypes: [],
  allowChatbot: false,
  allowDcmaPbp: false,
  allowDcmaCrr: false,
  canUseStandalonePriceEstimator: false,
  marketResearchView: 'unknown',
  showAdminNavigation: false,
  showUsageDashboard: false,
  showSuperAdminControls: false,
  showUserFeedback: false,
  allowChangelog: false,
  showAllWorkflows: false,
  readOnly: true,
  isRolePreview: isRolePreview(preview),
  allowTraining: false,
  allowRaTraining: false,
  userFeatureEntitlements: { allowTraining: false },
});

/**
 * Returns a client presentation projection of the server-owned role policy.
 */
export const getPresentationCapabilities = (
  requestedPreview: RolePreview | string,
  input: RolePreviewInput,
): PresentationCapabilities => {
  if (!isKnownPreview(requestedPreview) || !hasPermissions(input.permissions)) {
    return failClosedCapabilities(requestedPreview);
  }

  const permissions = input.permissions;
  const allowTraining = permissions.allow_training === true;
  return {
    allowedWorkflowTypes: permissions.allowed_workflow_types,
    allowChatbot: permissions.allow_chatbot === true,
    allowDcmaPbp: permissions.allow_dcma_pbp === true,
    allowDcmaCrr: permissions.allow_dcma_crr === true,
    canUseStandalonePriceEstimator: permissions.can_use_standalone_price_estimator === true,
    marketResearchView: permissions.market_research_view ?? 'unknown',
    showAdminNavigation: input.isAdmin === true,
    showUsageDashboard: input.isAdmin === true,
    showSuperAdminControls: input.isSuperAdmin === true,
    showUserFeedback: input.isSuperAdmin === true && input.isUserFeedbackStaff === true,
    allowChangelog: permissions.allow_changelog === true,
    showAllWorkflows: input.isSuperAdmin === true,
    readOnly: false,
    isRolePreview: isRolePreview(requestedPreview),
    allowTraining,
    allowRaTraining: permissions.allow_ra_training === true,
    userFeatureEntitlements: { allowTraining },
  };
};
